import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Select, Space, message, Modal, Form, Input, Tag, Popconfirm } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import FilterDrawer from '../components/FilterDrawer';
import { agentApi, agentDepotApi, type AgentDTO, type AgentDepotDTO } from '../api/kfc';
import { clientTablePagination } from '../utils/tablePagination';

type SearchValues = {
  agentMongoId?: string;
  depotId?: string;
  depotName?: string;
};

const matchContains = (hay?: string, needle?: string) =>
  !needle?.trim() || (hay ?? '').toLowerCase().includes(needle.trim().toLowerCase());

const AgentDepotList: React.FC = () => {
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [depots, setDepots] = useState<AgentDepotDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncAgentId, setSyncAgentId] = useState<string | null>(null);
  const [appliedSearch, setAppliedSearch] = useState<SearchValues>({});
  const [searchForm] = Form.useForm<SearchValues>();
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm] = Form.useForm<{
    agentId: string;
    depotId: string;
    depotName: string;
    depotAddr?: string;
    depotContact?: string;
  }>();

  const loadAgents = () => {
    agentApi.list().then((res) => setAgents(res.data)).catch(() => setAgents([]));
  };

  const loadDepots = (agentId?: string) => {
    setLoading(true);
    agentDepotApi
      .list(agentId)
      .then((res) => setDepots(res.data))
      .catch(() => setDepots([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAgents();
    loadDepots();
  }, []);

  const agentIdToIdentifier = (mongoId?: string) => {
    if (!mongoId) return undefined;
    const a = agents.find((x) => x.id === mongoId);
    return a?.basicInfo?.identifier ?? mongoId;
  };

  const agentOptions = agents.map((a) => ({
    value: a.id!,
    label: `${a.basicInfo?.name ?? ''} (${a.basicInfo?.identifier ?? a.id})`,
  }));

  const filterOption = (input: string, opt?: { label?: string }) =>
    (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase());

  const onSearch = () => {
    const values = searchForm.getFieldsValue();
    const next: SearchValues = {
      agentMongoId: values.agentMongoId,
      depotId: values.depotId?.trim() || undefined,
      depotName: values.depotName?.trim() || undefined,
    };
    setAppliedSearch(next);
    loadDepots(next.agentMongoId);
  };

  const onReset = () => {
    searchForm.resetFields();
    setAppliedSearch({});
    loadDepots();
  };

  const sync = (agentId: string) => {
    setSyncingId(agentId);
    agentDepotApi
      .syncFromAgent(agentId)
      .then((res) => {
        message.success(res.data.success ? res.data.message : (res.data.message || '同步失败'));
        if (res.data.success) loadDepots(appliedSearch.agentMongoId);
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncingId(null));
  };

  const openAdd = () => {
    addForm.resetFields();
    if (syncAgentId) addForm.setFieldsValue({ agentId: syncAgentId });
    else if (appliedSearch.agentMongoId) addForm.setFieldsValue({ agentId: appliedSearch.agentMongoId });
    setAddOpen(true);
  };

  const onAddOk = () => {
    addForm.validateFields().then((values) => {
      setAdding(true);
      agentDepotApi
        .create({
          agentId: values.agentId,
          depotId: values.depotId,
          depotName: values.depotName,
          depotAddr: values.depotAddr,
          depotContact: values.depotContact,
        })
        .then(() => {
          message.success('仓库已添加');
          setAddOpen(false);
          addForm.resetFields();
          loadDepots(appliedSearch.agentMongoId);
        })
        .catch((e) => message.error(e.response?.data?.message || e.message || '添加失败'))
        .finally(() => setAdding(false));
    });
  };

  const removeManual = (id: string) => {
    agentDepotApi
      .remove(id)
      .then(() => {
        message.success('已删除');
        loadDepots(appliedSearch.agentMongoId);
      })
      .catch((e) => message.error(e.response?.data?.message || e.message || '删除失败'));
  };

  const filteredDepots = useMemo(() => {
    const { agentMongoId, depotId, depotName } = appliedSearch;
    const ident = agentIdToIdentifier(agentMongoId);
    const agentIds = agentMongoId
      ? [agentMongoId, ident].filter((x): x is string => !!x)
      : null;

    return depots.filter((d) => {
      const agentOk = !agentIds || (d.agentId != null && agentIds.includes(d.agentId));
      return agentOk && matchContains(d.depotId, depotId) && matchContains(d.depotName, depotName);
    });
  }, [depots, appliedSearch, agents]);

  const isManual = (source?: string) => source === 'MANUAL';

  return (
    <>
      <Card
        title="代理仓库列表"
        extra={
          <Space wrap>
            <Select
              placeholder="选择代理"
              allowClear
              showSearch
              style={{ minWidth: 200 }}
              value={syncAgentId}
              onChange={setSyncAgentId}
              options={agentOptions}
              filterOption={filterOption}
            />
            <Button
              type="primary"
              onClick={() => syncAgentId && sync(syncAgentId)}
              loading={syncingId !== null}
              disabled={!syncAgentId}
            >
              同步仓库
            </Button>
            <Button onClick={openAdd}>手动添加仓库</Button>
          </Space>
        }
      >
        <FilterDrawer>
        <Form
          form={searchForm}
          layout="inline"
          style={{ marginBottom: 16 }}
          onFinish={onSearch}
        >
          <Form.Item name="agentMongoId" label="代理">
            <Select
              placeholder="代理名称或ID"
              allowClear
              showSearch
              style={{ minWidth: 220 }}
              options={agentOptions}
              filterOption={filterOption}
            />
          </Form.Item>
          <Form.Item name="depotId" label="仓库ID">
            <Input placeholder="模糊搜索" allowClear style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="depotName" label="仓库名称">
            <Input placeholder="模糊搜索" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                查找
              </Button>
              <Button onClick={onReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
        </FilterDrawer>

        <AdaptiveTable
          loading={loading}
          dataSource={filteredDepots}
          rowKey={(r) => r.id ?? `${r.agentId}-${r.depotId}`}
          scroll={{ x: 860 }}
          columns={[
            { title: '代理ID', dataIndex: 'agentId', width: 120, ellipsis: true },
            { title: '仓库ID', dataIndex: 'depotId', width: 100, ellipsis: true },
            { title: '仓库名称', dataIndex: 'depotName', width: 140, ellipsis: true },
            { title: '仓库地址', dataIndex: 'depotAddr', width: 200, ellipsis: true },
            { title: '仓库联系人/联系方式', dataIndex: 'depotContact', width: 160, ellipsis: true },
            {
              title: '来源',
              dataIndex: 'source',
              width: 110,
              render: (source: string | undefined) =>
                isManual(source) ? <Tag color="blue">手动添加</Tag> : <Tag>自动同步</Tag>,
            },
            {
              title: '操作',
              width: 90,
              fixed: 'right' as const,
              render: (_: unknown, r: AgentDepotDTO) =>
                isManual(r.source) && r.id ? (
                  <Popconfirm title="确认删除该手动仓库？" onConfirm={() => removeManual(r.id!)}>
                    <Button type="link" danger size="small">
                      删除
                    </Button>
                  </Popconfirm>
                ) : (
                  '-'
                ),
            },
          ]}
          pagination={clientTablePagination({ defaultPageSize: 20 })}
        />
      </Card>

      <Modal
        title="手动添加仓库"
        open={addOpen}
        onOk={onAddOk}
        onCancel={() => {
          setAddOpen(false);
          addForm.resetFields();
        }}
        confirmLoading={adding}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item name="agentId" label="代理" rules={[{ required: true, message: '请选择代理' }]}>
            <Select
              placeholder="选择代理"
              options={agentOptions}
              showSearch
              filterOption={filterOption}
            />
          </Form.Item>
          <Form.Item name="depotId" label="仓库ID" rules={[{ required: true, message: '请输入仓库ID' }]}>
            <Input placeholder="代理系统内仓库标识" />
          </Form.Item>
          <Form.Item name="depotName" label="仓库名称" rules={[{ required: true, message: '请输入仓库名称' }]}>
            <Input placeholder="仓库名称" />
          </Form.Item>
          <Form.Item name="depotAddr" label="仓库地址">
            <Input placeholder="可选" />
          </Form.Item>
          <Form.Item name="depotContact" label="仓库联系人/联系方式">
            <Input placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AgentDepotList;
